import React from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import {
  ExplorePeople,
  ExploreSort,
  ExploreTrip,
  budgetOptions,
  exploreRecommendations,
  exploreTags,
  exploreTrips,
  formatDaysAgo,
  getBudgetSymbol,
  peopleOptions,
  sortOptions,
} from '../../data/exploreTrips';
import './ExplorePage.css';

type FilterDropdown = 'country' | 'city' | 'budget' | 'people' | 'sort' | null;

interface ExploreFilters {
  country: string | null;
  city: string | null;
  tags: string[];
  budget: number | null;
  people: ExplorePeople | null;
  sort: ExploreSort;
}

const defaultFilters: ExploreFilters = {
  country: null,
  city: null,
  tags: [],
  budget: null,
  people: null,
  sort: 'newest',
};

const filterTrips = (trips: ExploreTrip[], filters: ExploreFilters) => {
  let list = trips.filter((trip) => {
    if (filters.country && trip.country !== filters.country) return false;
    if (filters.city && trip.city !== filters.city) return false;
    if (filters.budget && trip.budget !== filters.budget) return false;
    if (filters.people && trip.people !== filters.people) return false;
    if (filters.tags.length && !filters.tags.every((tag) => trip.tags.includes(tag))) return false;
    return true;
  });

  const sorters: Record<ExploreSort, (a: ExploreTrip, b: ExploreTrip) => number> = {
    newest: (a, b) => a.createdDaysAgo - b.createdDaysAgo,
    oldest: (a, b) => b.createdDaysAgo - a.createdDaysAgo,
    'most-saved': (a, b) => b.saves - a.saves,
    'budget-low': (a, b) => a.budget - b.budget,
    'budget-high': (a, b) => b.budget - a.budget,
  };

  list = [...list].sort(sorters[filters.sort]);
  return list;
};

const hasActiveFilters = (filters: ExploreFilters) =>
  Boolean(filters.country || filters.city || filters.budget || filters.people || filters.tags.length);

const ExplorePage: React.FC = () => {
  const [filters, setFilters] = React.useState<ExploreFilters>(defaultFilters);
  const [openDropdown, setOpenDropdown] = React.useState<FilterDropdown>(null);
  const [highlightedTripId, setHighlightedTripId] = React.useState<number | null>(null);
  const tripRefs = React.useRef<Record<number, HTMLButtonElement | null>>({});

  const countries = React.useMemo(
    () => [...new Set(exploreTrips.map((trip) => trip.country))].sort(),
    []
  );

  const cities = React.useMemo(() => {
    const scopedTrips = filters.country
      ? exploreTrips.filter((trip) => trip.country === filters.country)
      : exploreTrips;
    return [...new Set(scopedTrips.map((trip) => trip.city))].sort();
  }, [filters.country]);

  const filteredTrips = React.useMemo(() => filterTrips(exploreTrips, filters), [filters]);

  const recommendations = exploreRecommendations
    .map((item) => {
      const trip = exploreTrips.find((entry) => entry.id === item.tripId);
      if (!trip) return null;
      return { ...item, trip };
    })
    .filter(Boolean) as Array<{ tripId: number; why: string; trip: ExploreTrip }>;

  const toggleDropdown = (dropdown: FilterDropdown) => {
    setOpenDropdown((current) => (current === dropdown ? null : dropdown));
  };

  const updateFilters = (patch: Partial<ExploreFilters>) => {
    setFilters((current) => ({ ...current, ...patch }));
  };

  const toggleTag = (tag: string) => {
    setFilters((current) => ({
      ...current,
      tags: current.tags.includes(tag)
        ? current.tags.filter((entry) => entry !== tag)
        : [...current.tags, tag],
    }));
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
    setOpenDropdown(null);
  };

  const focusTrip = (tripId: number) => {
    setHighlightedTripId(tripId);
    tripRefs.current[tripId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => setHighlightedTripId(null), 1800);
  };

  React.useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.v2-explore-filter__menu')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  return (
    <DashboardLayout activeSection="explore" wide>
      <div className="v2-explore-page">
        <header className="v2-explore-page__hero">
          <h1>Find your next trip</h1>
          <p>Real itineraries from real travelers — browse, filter, and borrow what fits.</p>
        </header>

        <section className="v2-explore-page__recommendations" aria-labelledby="explore-recs-heading">
          <div className="v2-explore-page__section-head">
            <h2 id="explore-recs-heading">Based on your trips</h2>
          </div>
          <div className="v2-explore-page__recommendation-list">
            {recommendations.map(({ trip, why }) => (
              <button
                type="button"
                key={trip.id}
                className="v2-explore-rec-card"
                onClick={() => focusTrip(trip.id)}
              >
                <div className="v2-explore-rec-card__media">
                  <img src={trip.imageUrl} alt="" />
                  <span className="v2-explore-rec-card__why">{why}</span>
                </div>
                <div className="v2-explore-rec-card__body">
                  <h3>{trip.title}</h3>
                  <div className="v2-explore-rec-card__author">
                    <img src={trip.authorImg} alt="" />
                    <span>{trip.author}</span>
                  </div>
                  <p>{trip.city}, {trip.country} · {trip.duration}</p>
                  <div className="v2-explore-rec-card__tags">
                    {trip.tags.slice(0, 2).map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="v2-explore-page__community" aria-labelledby="explore-community-heading">
          <div className="v2-explore-page__section-head">
            <h2 id="explore-community-heading">Community trips</h2>
            <span>{filteredTrips.length} trips</span>
          </div>

          <div className="v2-explore-filters">
            <div className="v2-explore-filters__dropdowns">
              <FilterMenu
                label="Country"
                value={filters.country || 'All countries'}
                isOpen={openDropdown === 'country'}
                onToggle={() => toggleDropdown('country')}
              >
                <FilterOption selected={!filters.country} onSelect={() => updateFilters({ country: null, city: null })}>
                  All countries
                </FilterOption>
                {countries.map((country) => (
                  <FilterOption
                    key={country}
                    selected={filters.country === country}
                    onSelect={() => updateFilters({ country, city: null })}
                  >
                    {country}
                  </FilterOption>
                ))}
              </FilterMenu>

              <FilterMenu
                label="City"
                value={filters.city || 'All cities'}
                isOpen={openDropdown === 'city'}
                onToggle={() => toggleDropdown('city')}
              >
                <FilterOption selected={!filters.city} onSelect={() => updateFilters({ city: null })}>
                  All cities
                </FilterOption>
                {cities.map((city) => (
                  <FilterOption
                    key={city}
                    selected={filters.city === city}
                    onSelect={() => updateFilters({ city })}
                  >
                    {city}
                  </FilterOption>
                ))}
              </FilterMenu>

              <FilterMenu
                label="Budget"
                value={filters.budget ? budgetOptions.find((option) => option.value === filters.budget)?.label || 'Budget' : 'Any budget'}
                isOpen={openDropdown === 'budget'}
                onToggle={() => toggleDropdown('budget')}
              >
                <FilterOption selected={!filters.budget} onSelect={() => updateFilters({ budget: null })}>
                  Any budget
                </FilterOption>
                {budgetOptions.map((option) => (
                  <FilterOption
                    key={option.value}
                    selected={filters.budget === option.value}
                    onSelect={() => updateFilters({ budget: option.value })}
                  >
                    {option.label}
                  </FilterOption>
                ))}
              </FilterMenu>

              <FilterMenu
                label="Travelers"
                value={filters.people ? peopleOptions.find((option) => option.value === filters.people)?.label || 'Any group' : 'Any group'}
                isOpen={openDropdown === 'people'}
                onToggle={() => toggleDropdown('people')}
              >
                <FilterOption selected={!filters.people} onSelect={() => updateFilters({ people: null })}>
                  Any group
                </FilterOption>
                {peopleOptions.map((option) => (
                  <FilterOption
                    key={option.value}
                    selected={filters.people === option.value}
                    onSelect={() => updateFilters({ people: option.value })}
                  >
                    {option.label}
                  </FilterOption>
                ))}
              </FilterMenu>

              <FilterMenu
                label="Sort"
                value={sortOptions.find((option) => option.value === filters.sort)?.label || 'Newest'}
                isOpen={openDropdown === 'sort'}
                onToggle={() => toggleDropdown('sort')}
              >
                {sortOptions.map((option) => (
                  <FilterOption
                    key={option.value}
                    selected={filters.sort === option.value}
                    onSelect={() => updateFilters({ sort: option.value })}
                  >
                    {option.label}
                  </FilterOption>
                ))}
              </FilterMenu>

              {hasActiveFilters(filters) && (
                <button type="button" className="v2-explore-filters__clear" onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>

            <div className="v2-explore-filters__tags">
              {exploreTags.map((tag) => (
                <button
                  type="button"
                  key={tag}
                  className={`v2-explore-filters__tag ${filters.tags.includes(tag) ? 'v2-explore-filters__tag--active' : ''}`}
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {filteredTrips.length === 0 ? (
            <div className="v2-explore-page__empty">
              <h3>No trips match those filters</h3>
              <p>Try widening your search or clearing a filter.</p>
              <button type="button" onClick={clearFilters}>Clear all filters</button>
            </div>
          ) : (
            <div className="v2-explore-page__grid">
              {filteredTrips.map((trip) => (
                <button
                  type="button"
                  key={trip.id}
                  ref={(element) => {
                    tripRefs.current[trip.id] = element;
                  }}
                  className={`v2-explore-trip-card ${highlightedTripId === trip.id ? 'v2-explore-trip-card--highlight' : ''}`}
                  onClick={() => focusTrip(trip.id)}
                >
                  <div className="v2-explore-trip-card__media">
                    <img src={trip.imageUrl} alt="" />
                    <span className="v2-explore-trip-card__saves">♥ {trip.saves}</span>
                    <div className="v2-explore-trip-card__tag-row">
                      {trip.tags.slice(0, 2).map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="v2-explore-trip-card__body">
                    <h3>{trip.title}</h3>
                    <div className="v2-explore-trip-card__author">
                      <img src={trip.authorImg} alt="" />
                      <span>{trip.author}</span>
                    </div>
                    <p>{trip.city}, {trip.country}</p>
                    <div className="v2-explore-trip-card__meta">
                      <span>{trip.duration}</span>
                      <span>{getBudgetSymbol(trip.budget)}</span>
                      <span>{formatDaysAgo(trip.createdDaysAgo)}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
};

interface FilterMenuProps {
  label: string;
  value: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const FilterMenu: React.FC<FilterMenuProps> = ({ label, value, isOpen, onToggle, children }) => (
  <div className="v2-explore-filter__menu">
    <button type="button" className={`v2-explore-filter__trigger ${isOpen ? 'v2-explore-filter__trigger--open' : ''}`} onClick={onToggle}>
      <span>{label}</span>
      <strong>{value}</strong>
      <span aria-hidden>▾</span>
    </button>
    {isOpen && <div className="v2-explore-filter__dropdown">{children}</div>}
  </div>
);

interface FilterOptionProps {
  selected: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}

const FilterOption: React.FC<FilterOptionProps> = ({ selected, onSelect, children }) => (
  <button
    type="button"
    className={`v2-explore-filter__option ${selected ? 'v2-explore-filter__option--selected' : ''}`}
    onClick={onSelect}
  >
    {children}
  </button>
);

export default ExplorePage;
